import { useEffect, useRef, useState } from "react";
import { Search, ScanLine, Loader2, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { TRY, NUM, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import BarcodeScanner from "@/components/BarcodeScanner";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { pickOcc } from "@/components/returns/util";
import { LatestBadge, PriceHistory } from "@/components/returns/PriceHistory";

function ResultRow({ g, i, active, showSupplier, onPick }) {
  const o = pickOcc(g);
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(g)}
      className={cn("w-full text-left px-3 py-2 flex items-start gap-3 border-b last:border-0", active ? "bg-orange-50" : "hover:bg-muted/50")} data-testid={`ret-result-${i}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[13px] leading-snug truncate">{g.name}</div>
        <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {g.code && <span className="font-mono text-foreground/70">{g.code}</span>}
          {showSupplier && <span className="truncate max-w-[240px]">{g.supplier}</span>}
          {o && <span className="inline-flex items-center gap-1"><LatestBadge />{fmtDate(o.date)} · <span className="font-mono">{o.belge_no}</span> · <b className="text-foreground/80">{NUM(o.quantity)} {g.unit} alındı</b></span>}
          {o && (o.returned > 0 || o.in_draft > 0) && <span className="text-amber-700">önceki iade {NUM(o.returned + o.in_draft)}</span>}
          {g.count > 1 && <span className="text-orange-700">{g.count} faturada var</span>}
        </div>
        <PriceHistory occs={g.occurrences} unit={g.unit} max={3} testid={`ret-result-history-${i}`} />
      </div>
      {o && <div className="text-right shrink-0 leading-tight"><div className="font-bold tabular-nums text-[14px] text-emerald-700" data-testid={`ret-result-price-${i}`}>{TRY(o.net_unit)}</div><div className="text-[10px] text-muted-foreground">son fiyat · +KDV %{NUM(o.tax_rate)}</div></div>}
    </button>
  );
}

/** Ürün kodu / adı / barkod → ürünün geldiği alış faturaları. Enter: en son alınan fatura eklenir. */
export function ReturnSearch({ vkn, ver, exclude, onAdd }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ q: "", groups: [] });
  const [loading, setLoading] = useState(false);
  const [act, setAct] = useState(0);
  const [focus, setFocus] = useState(false);
  const [scan, setScan] = useState(false);
  const seq = useRef(0);

  const run = async (text) => {
    const s = ++seq.current;
    if (!text.trim()) { setRes({ q: "", groups: [] }); return []; }
    setLoading(true);
    try {
      const { data } = await api.get("/qnb/returns/search", { params: { q: text, vkn: vkn || undefined, limit: 30, exclude: exclude || undefined } });
      if (s === seq.current) { setRes({ q: text, groups: data.groups }); setAct(0); }
      return data.groups;
    } catch (e) { toast.error(apiError(e)); return []; }
    finally { if (s === seq.current) setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(() => run(q), 250);
    return () => clearTimeout(t);
  }, [q, vkn, ver]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (g) => { if (onAdd(g)) { setQ(""); setRes({ q: "", groups: [] }); } };
  const addBest = async (text) => {
    const list = res.q === text ? res.groups : await run(text);
    const g = list[act] || list[0];
    if (g) pick(g); else toast.error(`"${text}" için alış faturası bulunamadı`);
  };
  const onKey = (e) => {
    const n = res.groups.length;
    if (e.key === "ArrowDown" && n) { e.preventDefault(); setAct((a) => (a + 1) % n); }
    else if (e.key === "ArrowUp" && n) { e.preventDefault(); setAct((a) => (a - 1 + n) % n); }
    else if (e.key === "Enter" && q.trim()) { e.preventDefault(); addBest(q.trim()); }
    else if (e.key === "Escape") setQ("");
  };
  const onScan = async (code) => {
    const list = await run(code);
    if (list[0]) pick(list[0]); else toast.error(`${code}: alış faturası bulunamadı`);
  };
  const open = focus && q.trim() && (res.q === q || loading);

  return (
    <div className="relative" data-testid="ret-search">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
            placeholder="Ürün kodu, adı veya barkod yazın — Enter: en son alındığı fatura eklenir" className="h-11 pl-9 pr-9 rounded-xl text-[14px]" data-testid="ret-search-input" />
          {loading ? <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            : q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted" aria-label="Temizle" data-testid="ret-search-clear"><X className="h-3.5 w-3.5" /></button>}
        </div>
        <Button type="button" variant="secondary" className="h-11 rounded-xl px-3.5 text-white" onClick={() => setScan(true)} data-testid="ret-scan-btn"><ScanLine className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Barkod</span></Button>
      </div>
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-[380px] overflow-y-auto rounded-xl border bg-card shadow-xl" data-testid="ret-results">
          {res.groups.map((g, i) => <ResultRow key={g.key} g={g} i={i} active={i === act} showSupplier={!vkn} onPick={pick} />)}
          {!loading && res.groups.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground text-center" data-testid="ret-no-result">Bu koda / ada ait alış faturası kalemi bulunamadı</div>}
        </div>
      )}
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onDetected={onScan} continuous />
    </div>
  );
}
